import {
  Banknote,
  Wallet,
  Fuel,
  Gauge,
  PiggyBank,
  Gift,
  CalendarCheck,
  Users,
  TrendingUp,
  Clock,
  CheckCircle2,
  Target,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '../../lib/shadcn/utils'
import {
  DAILY_GROSS_TARGET_ZAR,
  formatHours,
  formatInt,
  formatPct,
  formatRand,
  hoursForShiftTarget,
  verdict,
  verdictClass,
  type Kpis,
  type Targets,
} from '../../utils/performance'

type Caption = { text: string; className: string }

type CardView = {
  key: string
  label: string
  icon: LucideIcon
  display: string
  valueClass: string
  caption: Caption | null
}

const MUTED = 'text-muted-foreground'
const NOT_MEASURED: Caption = { text: 'not measured yet', className: MUTED }
const NOT_RECORDED: Caption = { text: 'not recorded', className: MUTED }

// A missing total shows the no-value marker with "not recorded" under it, so
// it cannot be read as R 0 (B16).
function moneyCard(key: string, label: string, icon: LucideIcon, value: number | null): CardView {
  if (value === null) return { key, label, icon, display: formatRand(null), valueClass: MUTED, caption: NOT_RECORDED }
  return { key, label, icon, display: formatRand(value), valueClass: 'text-foreground', caption: null }
}

function signedMoneyCard(
  key: string,
  label: string,
  icon: LucideIcon,
  value: number | null,
): CardView {
  if (value === null) return { key, label, icon, display: formatRand(null), valueClass: MUTED, caption: NOT_RECORDED }
  const valueClass =
    value !== 0
      ? value > 0
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-red-600 dark:text-red-400'
      : 'text-foreground'
  return { key, label, icon, display: formatRand(value), valueClass, caption: null }
}

function intCard(key: string, label: string, icon: LucideIcon, value: number): CardView {
  return { key, label, icon, display: formatInt(value), valueClass: 'text-foreground', caption: null }
}

function buildCards(kpis: Kpis, targets: Targets | null): CardView[] {
  // Earnings per shift: colour good at/above target, warn at/above minimum,
  // bad below. Falls back to the legacy constant when live targets are absent.
  const eps = kpis.earnings_per_shift
  const epsTarget = targets?.earnings_per_shift.target ?? DAILY_GROSS_TARGET_ZAR
  const epsMinimum = targets?.earnings_per_shift.minimum ?? DAILY_GROSS_TARGET_ZAR
  const epsClass =
    eps === null
      ? MUTED
      : eps >= epsTarget
      ? 'text-emerald-600 dark:text-emerald-400'
      : eps >= epsMinimum
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-red-600 dark:text-red-400'
  const epsPct = eps === null ? null : Math.round((eps / epsTarget) * 100)

  const cards: CardView[] = [
    moneyCard('uber_gross', 'Uber gross', Banknote, kpis.uber_gross),
    moneyCard('driver_net', 'Driver net', Wallet, kpis.driver_net),
    moneyCard('petrol_cost', 'Petrol cost', Fuel, kpis.petrol_cost),
    moneyCard('petrol_budget', 'Petrol budget', Gauge, kpis.petrol_budget),
    signedMoneyCard('fuel_saving', 'Fuel saving', PiggyBank, kpis.fuel_saving),
    signedMoneyCard('driver_extras', 'Driver extras', Gift, kpis.driver_extras),
    intCard('shifts', 'Shifts', CalendarCheck, kpis.shifts),
    intCard('active_drivers', 'Active drivers', Users, kpis.active_drivers),
    {
      key: 'earnings_per_shift',
      label: 'Earnings / shift',
      icon: TrendingUp,
      display: formatRand(eps),
      valueClass: 'text-foreground',
      caption:
        epsPct === null
          ? NOT_RECORDED
          : { text: `${epsPct}% of ${formatRand(epsTarget)} target`, className: epsClass },
    },
  ]

  // Online hours and acceptance are the two boundaries (db/migrations/5814): hours decide most
  // of what a shift earns, acceptance decides what each hour pays.
  const oh = targets?.online_hours
  cards.push({
    key: 'avg_online_hours',
    label: 'Avg online hours',
    icon: Clock,
    display: formatHours(kpis.avg_online_hours),
    valueClass: verdictClass(verdict(kpis.avg_online_hours, oh?.target, 'higher_better')),
    caption:
      kpis.avg_online_hours === null
        ? NOT_MEASURED
        : oh
          ? { text: `minimum ${oh.minimum} h, target ${oh.target} h`, className: MUTED }
          : null,
  })

  const acc = targets?.acceptance_rate
  cards.push({
    key: 'acceptance_rate',
    label: 'Acceptance rate',
    icon: CheckCircle2,
    display: formatPct(kpis.acceptance_rate),
    valueClass: verdictClass(verdict(kpis.acceptance_rate, acc?.target, 'higher_better')),
    caption:
      kpis.acceptance_rate === null
        ? NOT_MEASURED
        : acc
          ? {
              text: `minimum ${formatPct(acc.minimum ?? null)}, target ${formatPct(acc.target ?? null)}`,
              className: MUTED,
            }
          : null,
  })

  // The hours a shift needs for R1,200 at the acceptance actually measured. The caption is
  // coloured by whether the hours online reach it, so the two levers read as one boundary.
  const model = targets?.earnings_model
  const needed = hoursForShiftTarget(model, kpis.acceptance_rate)
  cards.push({
    key: 'hours_for_target',
    label: `Hours for ${formatRand(model?.shift_target ?? DAILY_GROSS_TARGET_ZAR)}`,
    icon: Target,
    display: formatHours(needed),
    valueClass: 'text-foreground',
    caption:
      needed === null
        ? NOT_MEASURED
        : {
            text: `at ${formatPct(kpis.acceptance_rate)} acceptance, online ${formatHours(kpis.avg_online_hours)}`,
            className: verdictClass(verdict(kpis.avg_online_hours, needed, 'higher_better')),
          },
  })

  return cards
}

export function KpiCards({
  kpis,
  targets,
  loading,
}: {
  kpis: Kpis
  targets?: Targets | null
  loading: boolean
}) {
  const cards = buildCards(kpis, targets ?? null)
  const showCoverageNote =
    !loading && kpis.shifts_with_activity < kpis.shifts_excl_cancelled

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {cards.map((c) => {
          const Icon = c.icon
          return (
            <div
              key={c.key}
              className="rounded-lg border border-border bg-card p-4 shadow-retool-sm"
            >
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon className="h-4 w-4" />
                <span className="text-xs font-medium">{c.label}</span>
              </div>
              <div
                className={cn(
                  'mt-2 text-xl font-semibold tabular-nums',
                  loading ? 'text-muted-foreground' : c.valueClass,
                )}
              >
                {loading ? '…' : c.display}
              </div>
              {c.caption && (
                <div
                  className={cn(
                    'mt-1 text-xs font-medium tabular-nums',
                    loading ? 'text-muted-foreground' : c.caption.className,
                  )}
                >
                  {loading ? '\u00a0' : c.caption.text}
                </div>
              )}
            </div>
          )
        })}
      </div>
      {showCoverageNote && (
        <p className="mt-2 text-xs text-muted-foreground">
          Online hours and acceptance are measured on{' '}
          {formatInt(kpis.shifts_with_activity)} of {formatInt(kpis.shifts_excl_cancelled)}{' '}
          shifts. The rest are waiting on an Uber report.
        </p>
      )}
    </div>
  )
}
